/**
 * English: Compose
 *
 * What this file covers: Writing a single message to one person or a few, outside a conversation.
 *
 * Tone: Friendly and direct. Make clear who will receive the message and on which channel.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const compose: Catalog = {
  'compose.title': 'New message',
  'compose.description': 'Message one person, or a few. Pick them by name or number, and the reply lands in your inbox.',
  'compose.step.who': 'Who it goes to',
  'compose.step.what': 'What it says',
  'compose.step.send': 'Cost and send',
  'compose.number.label': 'Send from',
  'compose.number.hint': 'The number this message comes from. The reply lands in that number\'s inbox.',
  'compose.recipients.label': 'To',
  'compose.recipients.hint': 'Search a name or a number, or type a number that is not saved yet.',
  'compose.recipients.placeholder': 'Name or number',
  'compose.recipients.remove': 'Remove {{who}}',
  'compose.recipients.notInBook': 'Not in your contacts yet',
  'compose.recipients.noName': 'No name saved',
  'compose.recipients.noMatches': 'Nobody matches that. Type a full number to message somebody new.',
  'compose.recipients.count': '{{count}} of {{max}} people chosen',
  'compose.recipients.clear': 'Clear all',
  'compose.recipients.full.title': 'That is as many as this takes',
  'compose.recipients.full.body': 'A message written here goes to at most {{max}} people. For more than that, send a broadcast to a tag or a segment.',
  'compose.window.badge.open': 'Can receive a message',
  'compose.window.badge.needsTemplate': 'Needs a template',
  'compose.window.badge.templateOnly': 'Template only',
  'compose.window.badge.optedOut': 'Opted out',
  'compose.window.why.openHours': 'Wrote to you, {{hours}} h left to reply freely',
  'compose.window.why.openMinutes': 'Wrote to you, {{minutes}} min left to reply freely',
  'compose.window.why.never': 'Has never written to you',
  'compose.window.why.expiredDays': 'Last heard from them {{days}} days ago',
  'compose.window.why.expiredHours': 'Last heard from them {{hours}} hours ago',
  'compose.window.why.optedOut': 'Asked not to receive messages',
  'compose.window.closed.title': 'This one needs an approved template',
  'compose.window.closed.body': 'WhatsApp only allows a message in your own words within 24 hours of somebody writing to you. Choose an approved template instead.',
  'compose.window.mixed.title': 'These people are not all in the same position',
  'compose.window.mixed.body': '{{closed}} of these {{total}} have not written to you in the last 24 hours, and one message goes to all of them, so this has to be an approved template.',
  'compose.window.optedOut': '{{count}} of the people chosen have asked not to receive messages. They are left out and not charged for.',
  'compose.message.label': 'Message',
  'compose.message.hint': 'In your own words. Type {{ to add something about each person, such as their name.',
  'compose.message.placeholder': 'Write your message',
  'compose.message.count': '{{length}} of {{max}}',
  'compose.message.previewEmpty': 'Your message will show here',
  'compose.message.previewBusiness': 'Your business',
  'compose.template.label': 'Template',
  'compose.template.hint': 'Only templates WhatsApp has approved can be sent outside the 24-hour window.',
  'compose.template.placeholders': 'Fill in the blanks',
  'compose.template.placeholders.hint': 'Type {{ to fill a blank from each person’s contact card, such as their name. Each person then gets their own value.',
  'compose.template.placeholder': 'Blank {{n}}',
  'compose.template.noPlaceholders': 'This template has no blanks to fill in.',
  'compose.template.noneApproved.title': 'No approved templates yet',
  'compose.template.noneApproved.body': 'WhatsApp has to approve a template before it can be sent to somebody who has not written to you recently.',
  'compose.template.noneApproved.action': 'Create a template',
  'compose.template.devFallbackHidden': '{{count}} templates are hidden because WhatsApp has not approved them yet.',
  'compose.summary.recipients': 'People',
  'compose.summary.excluded': '{{count}} left out',
  'compose.summary.cost': 'This will cost',
  'compose.summary.balance': 'Balance',
  'compose.summary.balanceAfter': '{{after}} left after sending',
  'compose.summary.balanceUnknown': 'Choose who it goes to and we will work this out.',
  'compose.summary.short.title': 'Not enough credits',
  'compose.summary.short.body': 'You need {{shortfall}} more to send this.',
  'compose.summary.short.action': 'Top up',
  'compose.summary.broadcastHint': 'Messaging a lot of people?',
  'compose.summary.broadcastAction': 'Send a broadcast',
  'compose.action.send': 'Send to {{count}}',
  'compose.action.sendTemplate': 'Send template to {{count}}',
  'compose.error.bodyRequired': 'Write the message before sending it.',
  'compose.error.templateRequired': 'Choose an approved template.',
  'compose.error.placeholderRequired': 'Fill this in before sending.',
  'compose.error.numbersFailed': 'Your WhatsApp numbers could not be loaded just now.',
  'compose.error.noNumbers.title': 'No WhatsApp number connected',
  'compose.error.noNumbers.body': 'A message has to come from a connected number. Connect one and you can message anybody.',
  'compose.error.noNumbers.action': 'Connect a number',
  'compose.refusal.windowClosed': 'Some of these people have not written to you in the last 24 hours, so this has to be an approved template. Nothing was sent.',
  'compose.refusal.TOO_MANY_RECIPIENTS': 'That is more people than a message written here takes. Send a broadcast to a tag or a segment instead.',
  'compose.refusal.NUMBER_REQUIRED': 'Say which of your numbers this message comes from.',
  'compose.refusal.NO_ACTIVE_NUMBER': 'No WhatsApp number is connected yet, so nothing can be sent.',
  'compose.refusal.VALIDATION': 'Something in this message is not filled in yet.',
  'compose.refusal.unknown': 'This could not be sent just now. Try again in a moment.',
  'compose.toast.sent': 'Sent to {{count}}.',
  'compose.toast.partial': 'Sent to {{sent}}, but {{failed}} did not go through.',
  'compose.toast.noneSent': 'Nothing was sent. See below for what happened.',
  'compose.results.allSent.title': 'All sent',
  'compose.results.allSent.body': 'This reached all {{count}} of them. Their replies will land in your inbox.',
  'compose.results.noneSent.title': 'Nothing was sent',
  'compose.results.noneSent.body': 'None of the {{count}} people were reached. What went wrong is shown against each of them.',
  'compose.results.partial.title': 'Some of these did not go through',
  'compose.results.partial.body': '{{sent}} were reached and {{failed}} were not. You can try the ones that failed again without sending to anybody twice.',
  'compose.results.delivered': 'On its way',
  'compose.results.open': 'Open chat',
  'compose.results.badge.sent': 'Sent',
  'compose.results.badge.failed': 'Not sent',
  'compose.results.backToInbox': 'Back to inbox',
  'compose.results.another': 'Write another',
  'compose.results.retry': 'Try {{count}} again',
  'compose.results.reason.unknown': 'This one did not go through.',
  'compose.results.reason.OPTED_OUT': 'They have asked not to receive messages.',
  'compose.results.reason.WINDOW_CLOSED': 'They have not written to you in the last 24 hours, so this needed an approved template.',
  'compose.results.reason.INSUFFICIENT_CREDITS': 'There were not enough credits left to send this one.',
  'compose.results.reason.PHONE_NOT_FOUND': 'The number this was sent from is no longer connected.',
  'compose.results.reason.CHANNEL_UNREACHABLE': 'WhatsApp could not be reached. Nothing was sent to them.',
  'compose.results.reason.SEND_FAILED': 'WhatsApp did not accept this one.',
  'compose.results.reason.META_REFUSED': 'WhatsApp did not accept this one.',
  'compose.results.reason.SEND_UNREADABLE': 'This went out but we could not confirm what happened to it.',
  'compose.number.unnamed': 'A number still being set up',
  'compose.results.reason.ACCOUNT_NOT_PAYABLE': 'WhatsApp turned this down over a billing problem on our side, not yours. We are fixing it, and you do not need to do anything at Meta.',
  'compose.results.reason.UNDELIVERABLE': 'WhatsApp could not deliver this to that number.',
  'compose.results.reason.NUMBER_NOT_REGISTERED': 'The number you sent from is not registered with WhatsApp.',
  'compose.results.reason.MEDIA_NOT_FETCHED': 'WhatsApp could not fetch the attached file.',
  'compose.recipients.noNumber': 'No phone number, so WhatsApp cannot reach them.',
  'compose.results.reason.ACCOUNT_NOT_PAYABLE.customer': 'WhatsApp turned this down because your WhatsApp account has no working payment method in Meta. Add one in Meta\'s payment settings.',
  'compose.results.reason.OWN_ACCOUNT_NOT_ON_PLAN': 'Your plan no longer includes your own WhatsApp account. Move to Growth or Scale to keep sending from this number.',
  'compose.results.reason.NUMBER_DAILY_LIMIT': 'The sending number had reached WhatsApp\'s daily limit on new people.'
};

export default compose;
